import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { createQueryKeys, type QueryKeyPart } from "@nasam/kernel";
import type {
  SupervisorAssignmentCreateInput,
  SupervisorAssignmentHistoryQuery,
  SupervisorAssignmentsListQuery,
  SupervisorAssignmentsRepository,
  SupervisorAssignmentUpdateInput,
} from "../domain";

const supervisorAssignmentsQueryKeys = createQueryKeys(
  "fleet-supervisor-assignments",
);

function params(
  query: SupervisorAssignmentsListQuery | SupervisorAssignmentHistoryQuery | undefined,
): Readonly<Record<string, QueryKeyPart>> | undefined {
  return query === undefined ? undefined : { ...query };
}

export function useSupervisorAssignmentsQuery(
  repository: SupervisorAssignmentsRepository,
  query?: SupervisorAssignmentsListQuery,
) {
  return useQuery({
    queryKey: supervisorAssignmentsQueryKeys.list(params(query)),
    queryFn: () => repository.list(query),
  });
}

export function useSupervisorAssignmentQuery(
  repository: SupervisorAssignmentsRepository,
  supervisorId: string,
) {
  return useQuery({
    queryKey: supervisorAssignmentsQueryKeys.detail(supervisorId),
    queryFn: () => repository.getById(supervisorId),
    enabled: supervisorId.length > 0,
  });
}

/** No pagination/search -- the real endpoint returns the full eligible list
 *  in one shot; the Combobox filters it locally (sync mode). `enabled` lets
 *  the Edit form skip this fetch entirely -- its Supervisor field is locked
 *  to the id being edited, not picked from this list. */
export function useEligibleSupervisorsQuery(
  repository: SupervisorAssignmentsRepository,
  options?: { readonly enabled?: boolean },
) {
  return useQuery({
    queryKey: [...supervisorAssignmentsQueryKeys.all, "eligible-supervisors"],
    queryFn: () => repository.listEligibleSupervisors(),
    enabled: options?.enabled ?? true,
  });
}

export function useAssignableAssetsQuery(
  repository: SupervisorAssignmentsRepository,
  branchId: string | undefined,
) {
  return useQuery({
    queryKey: [...supervisorAssignmentsQueryKeys.all, "assignable-assets", branchId ?? null],
    queryFn: () => repository.listAssignableAssets(branchId),
  });
}

export function useCreateSupervisorAssignmentMutation(
  repository: SupervisorAssignmentsRepository,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: SupervisorAssignmentCreateInput) => repository.create(input),
    // A duplicate/ineligible-supervisor rejection throws (see
    // createSupervisorAssignmentsHttpRepository.create) and never reaches
    // here, so success always means something was actually created.
    onSuccess: () =>
      client.invalidateQueries({ queryKey: supervisorAssignmentsQueryKeys.all }),
  });
}

export function useUpdateSupervisorAssignmentMutation(
  repository: SupervisorAssignmentsRepository,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: SupervisorAssignmentUpdateInput) => repository.update(input),
    // Same reasoning as create's mutation: a duplicate/ineligible-supervisor
    // rejection throws and never reaches here.
    onSuccess: () =>
      client.invalidateQueries({ queryKey: supervisorAssignmentsQueryKeys.all }),
  });
}

export function useDeleteSupervisorAssignmentMutation(
  repository: SupervisorAssignmentsRepository,
) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => repository.delete(id),
    onSuccess: async () =>
      client.invalidateQueries({ queryKey: supervisorAssignmentsQueryKeys.all }),
  });
}

export function useSupervisorAssignmentHistoryQuery(
  repository: SupervisorAssignmentsRepository,
  query?: SupervisorAssignmentHistoryQuery,
) {
  return useQuery({
    queryKey: [...supervisorAssignmentsQueryKeys.all, "history", params(query)],
    queryFn: () => repository.listHistory(query),
  });
}
